import { BehaviorSubject, Subject } from 'rxjs';
import MovesHistory from './movesHistory';
import Dice from './dice';
import DoubleCube from './doubleCube';
import Move from './move';
import Color from '@base/features/game/model/color';
import CheckerStack from './checkerStack';
import { t } from '@base/utils/helpers';
import Checker from './checker';
import BaseGameStateImpl from '@base/features/game/model/baseGameState';
import { TableSides } from '@base/features/game/model/types';
import { GameButton, IGameService, ITableRepository } from '@base/features/game/model/interfaces';
import { BackgammonPhases, BackgammonScoreTypes } from './types';
import { MessagesService } from '@base/features/chat/messagesService';
import { IPreferencesService } from '@base/features/preferences/model/types';
import { IPlayer, IPlayersOnlineRepository } from '@base/features/player/model/interfaces';
import { Preferences } from '@backgammon/constants/defaultPreferences';
import { IModalProvider } from '@base/features/modal/model/interfaces';
import Table from './table';
import SoundProvider from '@base/features/game/model/soundProvider';
import LobbyService from '@base/features/lobby/model/lobbyService';
import Codes from '@base/features/socket/codes';
import { ISocketClient } from '@base/features/socket/interfaces';

const GameAction = Codes.GameActions;

export default class GameStateImpl extends BaseGameStateImpl<Preferences, Table> {
  onCheckerMoved = new Subject<Move[] | Move>();
  onInvalidateEvent = new Subject<boolean>();
  stacks: CheckerStack[] = [];
  stacksCount = 28;
  movesHistory = new MovesHistory();
  dice = new Dice(this);
  doubleCube = new DoubleCube(this);
  diceSum: number[] = [];
  phase?: BackgammonPhases;
  continuePressed = false;
  modelSavedState: Record<string, any> = {};
  undos = 0;
  autoRejectAborts = false;
  autoRejectResigns = false;
  roundWinner?: Color = undefined;
  onTakeBackOffered = new BehaviorSubject(false);
  onTakeBackLockChanged = new BehaviorSubject(false);
  takebackOfferedToMe = false;
  lastWinnerSide: TableSides | null = null;
  takeBackLockTimer?: NodeJS.Timeout;

  constructor(
    table: Table,
    tablesRepository: ITableRepository,
    playersOnlineRepository: IPlayersOnlineRepository,
    lobbyService: LobbyService,
    soundProvider: SoundProvider<Preferences>,
    private preferencesService: IPreferencesService<Preferences>,
    messagesService: MessagesService<Preferences, Table>,
    modalProvier: IModalProvider,
    gameService: IGameService,
    socket: ISocketClient,
  ) {
    super(
      table,
      tablesRepository,
      playersOnlineRepository,
      lobbyService,
      messagesService,
      modalProvier,
      gameService,
      soundProvider,
      socket
    );
    this.createInitialStacks();
    this.score.NORTH = 0;
    this.score.SOUTH = 0;
    this.autoRejectAborts = false;
    this.autoRejectResigns = false;
    this.updateDoubleCube();
  }

  updateDoubleCube() {
    const maxScore = this.table.maxPoints;
    const crawford = this.table.minPoints === -1;
    const noDouble = this.table.minPoints === 0;
    this.doubleCube.setRules(maxScore, crawford, noDouble);
  }

  forceRenew() {
    this.emitInvalidate();
    this.dice.emitDicesChanged(false);
    this.doubleCube.emitDoubleCubeChangedEvent();
  }

  moveFromViewByClick(sourceLineNumber: number) {
    const color = this.getMyColor();
    // let fromBar = sourceLineNumber === 26 || sourceLineNumber === 27;

    let insertLineNumber: number | undefined = -1;

    const availableTurns = this.dice.getAvailableMoves();
    // let consumedTurns = this.dices.getConsumedMoves();

    const availableToPlaceNumbers = this.getAvailableMoves(
      sourceLineNumber,
      color,
      false,
      availableTurns,
      false,
      true
    );

    if (availableToPlaceNumbers) {
      if (availableToPlaceNumbers?.length === 0) {
        //can`t move forward from this line, try to undo

        if (
          this.movesHistory.getLastMove() &&
          this.movesHistory.getLastMove()?.getInsertLineNumber() === sourceLineNumber
        ) {
          insertLineNumber = this.movesHistory.getLastMove()?.sourceLineNumber;
          console.debug('undo move line number: ', this.movesHistory.getLastMove()?.sourceLineNumber);
        }
      } else if (availableToPlaceNumbers?.length === 1) {
        insertLineNumber = availableToPlaceNumbers[0];
      } else if (availableToPlaceNumbers?.length >= 2) {
        const firstNumber = availableToPlaceNumbers[0]!;
        const secondNumber = availableToPlaceNumbers[1]!;

        //chosing max turn
        const firstMove = new Move();
        firstMove.setSourceLineNumber(sourceLineNumber);
        firstMove.setInsertLineNumber(firstNumber);
        firstMove.color = color;

        const secondMove = new Move();
        secondMove.setSourceLineNumber(sourceLineNumber);
        secondMove.setInsertLineNumber(secondNumber);
        secondMove.color = color;

        insertLineNumber =
          this.getMoveLength(firstMove) > this.getMoveLength(secondMove) ? firstNumber : secondNumber;
      }
    }

    if (insertLineNumber !== -1) {
      const move = new Move();
      move.setSourceLineNumber(sourceLineNumber);
      if (insertLineNumber) move.setInsertLineNumber(insertLineNumber);
      move.color = color;
      this.moveFromView(move, true);
    }
  }

  moveFromView(move: Move, byClick?: boolean) {
    const allowedMove = true;

    if (allowedMove) {
      const usedTurns = this.convertToElementaryTurns(move);
      const elementaryMoves = [];

      let currentLineNumber = move.sourceLineNumber;
      const fromBar = move.sourceLineNumber === 26 || move.sourceLineNumber === 27;
      const toBar = move.insertLineNumber === 26 || move.insertLineNumber === 27;
      const dropOff = move.insertLineNumber === 0 || move.insertLineNumber === 25;
      let moveSign;
      if (fromBar) {
        moveSign = move.color === Color.Black ? 1 : -1;
      } else if (toBar) {
        moveSign = move.color === Color.Black ? 1 : -1;
      } else {
        moveSign = Math.sign(move.insertLineNumber - move.sourceLineNumber);
      }

      for (let i = 0; i < usedTurns.length; i++) {
        const m = new Move();
        m.sourceLineNumber = currentLineNumber;
        if (fromBar && i === 0) {
          m.insertLineNumber = move.color === Color.Black ? usedTurns[i] : 25 - usedTurns[i];
        } else if (toBar) {
          m.insertLineNumber = move.insertLineNumber;
        } else if (dropOff) {
          m.insertLineNumber = move.color === Color.Black ? 25 : 0;
        } else {
          m.insertLineNumber = currentLineNumber + moveSign * usedTurns[i];
        }
        m.color = move.color;
        elementaryMoves.push(m);
        currentLineNumber = m.insertLineNumber;
      }

      for (let i = 0; i < elementaryMoves.length; i++) {
        elementaryMoves[i] = this.applyMove(elementaryMoves[i]);
        this.sendMoveToServer(elementaryMoves[i]);
        //send this move
      }

      // copy move, no other method work for copy
      const movesCopy = [];
      for (let i = 0; i < elementaryMoves.length; i++) {
        const moveCopy = this.copyMove(elementaryMoves[i]);
        movesCopy.push(moveCopy);
      }
      this.refreshStatusMessage();
      this.emitCheckerMoved(movesCopy, byClick);
    }
  }

  sendMoveToServer(move: Move) {
    const moveString = move.toString();
    this.checkSendMoveTime(moveString);
    this.gameService.sendMove(
      moveString,
      this.getTable().tid,
      this.clock.getElapsedTime(this.getCurrentTurn())
    );
  }

  copyMove(move: Move) {
    const copy = new Move();
    copy.setInsertLineNumber(move.getInsertLineNumber());
    copy.setSourceLineNumber(move.getSourceLineNumber());
    copy.color = move.color;
    if (move.getAdditionalMoves().length > 0) {
      for (let i = 0; i < move.getAdditionalMoves().length; i++) {
        copy.addAdditionalMove(this.copyMove(move.getAdditionalMoves()[i]));
      }
    }
    return copy;
  }

  /**
   * separates move from view into turns that it use.
   * @param {Move} move Raw move, that should be separated to few moves, based on dices
   * @return {Array} array on moves that raw move consist of
   */
  convertToElementaryTurns(move: Move) {
    let result = [];
    const fromBar = move.sourceLineNumber === 26 || move.sourceLineNumber === 27;
    const toBar = move.insertLineNumber === 26 || move.insertLineNumber === 27;
    const dropOffBoard = move.insertLineNumber === 0 || move.insertLineNumber === 25;
    const validTurns = [];

    const forwardSign = move.color === Color.Black ? 1 : -1;
    const actualSign = Math.sign(move.insertLineNumber - move.sourceLineNumber);

    const isUndoMove = forwardSign !== actualSign && !fromBar;
    let moveLength = this.getMoveLength(move);

    if (fromBar) {
      moveLength = move.color === Color.Black ? move.insertLineNumber : 25 - move.insertLineNumber;
    }
    if (toBar) {
      //that is undo
      moveLength = move.color === Color.Black ? move.sourceLineNumber : 25 - move.sourceLineNumber;
    }

    if (this.dice.containsAvaialableMove(moveLength) || this.dice.containsConsumedMove(moveLength)) {
      //simple move
      const turns = [];
      turns.push(moveLength);
      validTurns.push(turns);
    } else if (dropOffBoard) {
      //that is dirty drop should use maximum length move
      const avTurns = this.dice.getAvailableMoves().sort((a, b) => {
        if (a > b) return -1;
        if (a < b) return 1;
        return 0;
      });
      const turns = [];
      turns.push(avTurns[0]); // max turn
      validTurns.push(turns);
    } else {
      //complex move
      let sum = 0;
      const usedTurns = [];

      //находим из каких ходов состоит движение
      for (let i = 0; i < this.dice.getAvailableMoves().length; i++) {
        sum += this.dice.getAvailableMoves()[i];
        usedTurns.push(this.dice.getAvailableMoves()[i]);
        if (sum === moveLength) {
          break;
        }
      }

      if (!isUndoMove) {
        //complex move
        //определяем возможные последовательности ходов
        if (usedTurns.length < 3) {
          //если ход на три или больше - > double, порядок следования не важен
          // проверить каждый составляющий ход на валидность
          for (let i = 0; i < usedTurns.length; i++) {
            if (this.canApplyTurns(move.sourceLineNumber, usedTurns, move.color)) {
              validTurns.push(usedTurns.slice()); //push copy
            }
            const temp: any = usedTurns[0];
            usedTurns.splice(0, 1);
            usedTurns.push(temp);
          }
        } else {
          validTurns.push(usedTurns);
        }

        //выбираем последовательность где больше ударов по фишкам.
        const scope = this;
        validTurns.sort(function (a, b) {
          const keyA = scope.getNumberOfHits(move.sourceLineNumber, a, move.color),
            keyB = scope.getNumberOfHits(move.sourceLineNumber, b, move.color);

          if (keyA > keyB) return -1;
          if (keyA < keyB) return 1;
          return 0;
        });
      }
    }

    result = validTurns[0];
    return result;
  }

  getNumberOfHits(sourceLineNumber: number, turns: number[], color: Color) {
    const fromBar = sourceLineNumber === 26 || sourceLineNumber === 27;
    const moveSign = color === Color.Black ? 1 : -1;
    let currentNum = sourceLineNumber;
    let hits = 0;
    for (let i = 0; i < turns.length; i++) {
      if (fromBar && i === 0) {
        currentNum = color === Color.Black ? turns[i] : 25 - turns[i];
      } else {
        currentNum += moveSign * turns[i];
      }
      if (this.isUnderAttack(this.stacks[currentNum], color)) {
        hits++;
      }
    }
    return hits;
  }

  canApplyTurns(sourceLineNumber: number, turns: number[], color: Color) {
    const fromBar = sourceLineNumber === 26 || sourceLineNumber === 27;
    let currentNum = sourceLineNumber;
    const moveSign = color === Color.Black ? 1 : -1;

    for (let i = 0; i < turns.length; i++) {
      if (fromBar && i === 0) {
        currentNum = color === Color.Black ? turns[i] : 25 - turns[i];
      } else {
        currentNum += moveSign * turns[i];
      }
      if (currentNum < 0 || currentNum > 25 || !this.canPlaceToLine(this.stacks[currentNum], color)) {
        return false;
      }
    }
    return true;
  }

  onMoveReceived(obj: any) {
    this.sound.play(
      this.sound.codes.MOVE,
      obj.player.getPid() === this.me.getPid()
        ? this.sound.categories.YOUR_MOVE
        : this.sound.categories.OPP_MOVE
    );

    if (obj.player.getPid() === this.me.getPid()) {
      this.checkReceiveMoveTime(obj.decoded);
      return;
    }

    let move = Move.fromString(obj.decoded);

    // TODO logger
    // Logger.addEventToLog("moves", { type: "moveFromServer", move: move});

    move = this.applyMove(move);

    const moves = [];
    moves.push(move);
    this.emitCheckerMoved(moves);
  }

  /**
   * applies move to model, and calcualate additional moves that first move causes
   * @param {Move} single move to apply to model, undo or move forward
   * @return {Move} new move, with additional moves, if any
   */
  applyMove(move: Move) {
    const forwardSign = move.color === Color.Black ? 1 : -1;
    const fromBar = move.sourceLineNumber === 26 || move.sourceLineNumber === 27;
    const toBar = move.insertLineNumber === 26 || move.insertLineNumber === 27;
    const dropOffBoard = move.insertLineNumber === 0 || move.insertLineNumber === 25;
    let actualSign;

    if (fromBar) {
      actualSign = forwardSign;
    } else if (toBar) {
      actualSign = -forwardSign;
    } else {
      actualSign = Math.sign(move.insertLineNumber - move.sourceLineNumber);
    }

    const isUndoMove = forwardSign !== actualSign && !dropOffBoard;
    if (isUndoMove) {
      //delete from move history
      move = this.movesHistory.undoLastMove(move); // this will get the move with its additional move
      move = this.makeUndoMove(move);
      this.dice.undoTurn(this.getMoveLength(move));
      this.increaseUndos();
    } else {
      move = this.makeForwardMove(move);
      this.movesHistory.addMove(move);

      let diceIndication = this.getMoveLength(move);
      let turnConsumed = this.dice.consumeTurn(this.getMoveLength(move));
      if (!turnConsumed) {
        if (dropOffBoard) {
          //используем максимальное из dice
          const maxDice = Math.max.apply(Math, this.dice.getAvailableMoves());
          diceIndication = maxDice;
          turnConsumed = this.dice.consumeTurn(maxDice);
        }
      }
      move.setDiceIndication(diceIndication);
      if (!turnConsumed) {
        throw new Error('cant consume turn!');
      }
    }

    switch (this.phase) {
      case BackgammonPhases.WAIT_FOR_OP:
      case BackgammonPhases.WATCHER:
      case BackgammonPhases.WAIT_FOR_OP1:
      case BackgammonPhases.FIRST_FROM_BAR:
      case BackgammonPhases.CONTINUE:
      case BackgammonPhases.CONTINUE1:
        if (this.imWatching()) {
          this.setPhase(BackgammonPhases.WATCHER);
        } else {
          this.setPhase(
            this.mySide === this.currentTurn ? BackgammonPhases.CONTINUE : BackgammonPhases.WAIT_FOR_OP
          );
        }
        this.analyzePosition(false);
        this.refreshStatusMessage();
        break;
    }
    // TODO logger
    // Logger.addEventToLog("board", { type: "state", board: this.toString()});
    console.debug(this.toString());
    return move;
  }

  makeForwardMove(move: Move) {
    if (this.isUnderAttack(this.stacks[move.insertLineNumber], move.color)) {
      //this move attacks his checker
      const additionalMove = new Move();
      const hisColor = Color.reverseColor(move.color);
      const hisBarNumber = hisColor === Color.Black ? 26 : 27;
      additionalMove.color = hisColor;
      additionalMove.setSourceLineNumber(move.insertLineNumber);
      additionalMove.setInsertLineNumber(hisBarNumber);
      move.addAdditionalMove(additionalMove);

      this.makeMove(additionalMove.sourceLineNumber, additionalMove.insertLineNumber);
    }
    this.makeMove(move.sourceLineNumber, move.insertLineNumber);
    return move;
  }

  makeUndoMove(move: Move) {
    this.makeMove(move.insertLineNumber, move.sourceLineNumber);
    if (move.getAdditionalMoves().length > 0) {
      // return his checker from bar

      this.makeMove(
        move.getAdditionalMoves()[0].insertLineNumber,
        move.getAdditionalMoves()[0].sourceLineNumber
      );
      const reverseMoveFromBar = Move.reverseMove(move.getAdditionalMoves()[0]);
      move = Move.reverseMove(move);
      move.clearAdditionalMoves();
      move.addAdditionalMove(reverseMoveFromBar);
    } else {
      move = Move.reverseMove(move);
    }
    return move;
  }

  makeMove(sourceLineNumber: number, insertLineNumber: number) {
    const myChecker = this.stacks[sourceLineNumber].detachChecker();
    this.stacks[insertLineNumber].attachChecker(myChecker);
  }

  /**
   * returns all available to place line numbers from sourve line
   * @param {Integer} sourceLineNumber source line number to check available moves
   * @param {Color} color Color of player trying to make move
   * @param {Boolean} withUndos add undo moves to available
   * @param {Array} available Dices numbers
   * @param {Boolean} print debug information
   * @param {Boolean} disallow moves with several dice
   * @return {Array} available line number to place
   */
  getAvailableMoves(
    sourceLineNumber: number,
    color: Color,
    withUndos: boolean,
    dice: number[],
    debug?: boolean,
    onlySingleDiceMoves?: boolean
  ) {
    if (debug) {
      console.debug('=== get available moves ===');
      console.debug('source line number: ' + sourceLineNumber);
    }

    if (!dice) {
      dice = this.getDice().getAvailableMoves();
    }

    if (this.stacks[sourceLineNumber].getCheckersCount() === 0) {
      return null;
    }

    const availableTurns = [];

    for (let i = 0; i < dice.length; i++) {
      availableTurns.push(dice[i]);
    }

    if (debug) {
      console.debug('availableTurns from dices: ', availableTurns.toString());
    }

    const availableLinesNumbers = [];
    const forwardSign = color === Color.Black ? 1 : -1;
    const fromBar = sourceLineNumber === 26 || sourceLineNumber === 27;
    const shouldMoveFromBar = this.getCheckersCountOnBar(color) !== 0;
    const hadMoreThanOneCheckerOnBar = this.getCheckersCountOnBar(color) > 1;

    if (debug) {
      console.debug('should move from bar: ', shouldMoveFromBar);
      console.debug('move from bar: ', fromBar);
    }

    if (shouldMoveFromBar && !fromBar) {
      //if you had checker on bar, you should always move from it;
      return null;
    }

    if (availableTurns.length === 0 && this.dice.getConsumedMoves().length === 0) {
      return null;
    }

    if (availableTurns.length === 2) {
      const restrictedTurns = this.getRestrictedTurns(availableTurns, sourceLineNumber, color);
      if (restrictedTurns.length === 1) {
        //можно сделать оба хода но в определенной последовательности
        if (debug) {
          console.debug(
            'have restricted turn, but can move two moves in special sequence, restricted turn: ',
            restrictedTurns[0]
          );
        }
        if (
          this.canUseTurnsFully(color, availableTurns) ||
          this.canUseTurnsFully(color, availableTurns.slice().reverse())
        ) {
          availableTurns.splice(availableTurns.indexOf(restrictedTurns[0]), 1);
        }
      } else if (restrictedTurns.length === 2) {
        //можно сделать только один ход (наибольший) с этой линии, надо проверить ходы с остальных линий
        if (this.canUseTurnsFully(color, availableTurns)) {
          //можем использовать ходы полностью, если будем ходить с другой линии
          if (debug) {
            console.debug(
              'have two restrcted turns from this line, but can use two turns if move from other line'
            );
          }
          return null;
        } else {
          //должны ходить наибольший ход,
          if (debug) {
            console.debug('have two restricted turns from this line, should use the biggest turn');
          }
          const minTurn = Math.min(restrictedTurns[0], restrictedTurns[1]);
          availableTurns.splice(availableTurns.indexOf(minTurn), 1);
        }
      }
    }
    if (debug) {
      console.debug('according to restricted turns, available turns are: ', availableTurns);
    }
    //calcualte forward single moves, and movesFromBar
    const availableLineNumbersForSingleMoves = this.getAvailableLineNumbersForSingleMoves(
      availableTurns,
      sourceLineNumber,
      color
    );
    for (let i = 0; i < availableLineNumbersForSingleMoves.length; i++) {
      availableLinesNumbers.push(availableLineNumbersForSingleMoves[i]);
    }

    if (debug) {
      console.debug('single moves, available line numbers: ', availableLinesNumbers);
    }

    if (!onlySingleDiceMoves) {
      // add Complex Moves
      // if bar had more than one checker, we  can move only one, so don`t calculate complex moves
      if (availableTurns.length === 2 && !hadMoreThanOneCheckerOnBar) {
        // проверяем по очереди возможность походить первой потом второй и наоборот, если какой то ход не дает затем сделать
        // второй, то исключаем его
        const firstMove = this.dice.getAvailableMoves()[0];
        const secondMove = this.dice.getAvailableMoves()[1];

        //firstMove then second
        let currentNumber;
        if (fromBar) {
          currentNumber = color === Color.Black ? firstMove : 25 - firstMove;
        } else {
          currentNumber = sourceLineNumber + forwardSign * firstMove;
        }

        if (!this.isDropOffBoard(currentNumber) && this.canPlaceToLine(this.stacks[currentNumber], color)) {
          currentNumber += forwardSign * secondMove;
          if (!this.isDropOffBoard(currentNumber) && this.canPlaceToLine(this.stacks[currentNumber], color)) {
            //можно ходить обоими ходами по очереди
            availableLinesNumbers.push(currentNumber);
          }
        }

        //secondMove then first
        if (fromBar) {
          currentNumber = color === Color.Black ? secondMove : 25 - secondMove;
        } else {
          currentNumber = sourceLineNumber + forwardSign * secondMove;
        }
        if (!this.isDropOffBoard(currentNumber) && this.canPlaceToLine(this.stacks[currentNumber], color)) {
          currentNumber += forwardSign * firstMove;
          if (!this.isDropOffBoard(currentNumber) && this.canPlaceToLine(this.stacks[currentNumber], color)) {
            //можно ходить обоими ходами по очереди
            if (availableLinesNumbers.indexOf(currentNumber) === -1) {
              availableLinesNumbers.push(currentNumber);
            }
          }
        }
      } else if (
        (availableTurns.length === 3 || availableTurns.length === 4) &&
        !hadMoreThanOneCheckerOnBar
      ) {
        // дубль
        const turn = availableTurns[0];
        const length = availableTurns.length;
        let startNum = 0;
        let canMoveFromBar = true;
        let currentNumber;
        if (shouldMoveFromBar && fromBar) {
          //одна фишка на баре, сначала считаем ее перенос, затем обычные ходы
          currentNumber = color === Color.Black ? turn : 25 - turn;
          if (!this.isDropOffBoard(currentNumber) && this.canPlaceToLine(this.stacks[currentNumber], color)) {
            availableLinesNumbers.push(currentNumber);
            startNum = 2;
          } else {
            //нельзя двигать с с бара
            canMoveFromBar = false;
          }
        } else {
          currentNumber = sourceLineNumber;
          startNum = 1;
        }
        if (canMoveFromBar) {
          for (let i = startNum; i <= length; i++) {
            currentNumber += forwardSign * turn;
            if (this.isDropOffBoard(currentNumber)) {
              //dont calculate drops here
              continue;
            }
            if (this.canPlaceToLine(this.stacks[currentNumber], color)) {
              availableLinesNumbers.push(currentNumber);
            } else {
              break;
            }
          }
        }
      }
      if (debug) {
        console.debug('added complex moves, available line numbers now: ', availableLinesNumbers);
      }
    }

    const availableDropOffNumbers = this.getAvailableLineNumbersForDropOffs(color, availableTurns);
    if (availableDropOffNumbers.length) {
      if (availableDropOffNumbers.indexOf(sourceLineNumber) !== -1) {
        const dropZoneNumber = color === Color.Black ? 25 : 0;
        availableLinesNumbers.push(dropZoneNumber);
      }
    }
    if (debug) {
      console.debug('added drops off board, available line numbers now: ', availableLinesNumbers);
    }

    if (withUndos && this.canUndo()) {
      //add last undo move only if we take from last insert
      if (
        this.movesHistory.getLastMove() &&
        this.movesHistory.getLastMove()?.getInsertLineNumber() === sourceLineNumber
      ) {
        availableLinesNumbers.push(this.movesHistory.getLastMove()?.sourceLineNumber);
        if (debug) {
          console.debug('undo move line number: ', this.movesHistory.getLastMove()?.sourceLineNumber);
        }
      }
    }

    return availableLinesNumbers;
  }

  getAvailableLineNumbersForSingleMoves(availableTurns: number[], sourceLineNumber: number, color: Color) {
    const forwardSign = color === Color.Black ? 1 : -1;
    const availableLinesNumbers = [];
    const fromBar = sourceLineNumber === 26 || sourceLineNumber === 27;
    for (let i = 0; i < availableTurns.length; i++) {
      let currentLineNumber;
      if (!fromBar) {
        currentLineNumber = sourceLineNumber + forwardSign * availableTurns[i];
      } else {
        currentLineNumber = color === Color.Black ? availableTurns[i] : 25 - availableTurns[i];
      }

      if (this.isDropOffBoard(currentLineNumber)) {
        continue;
      }

      if (this.canPlaceToLine(this.stacks[currentLineNumber], color)) {
        availableLinesNumbers.push(currentLineNumber);
      }
    }
    return availableLinesNumbers;
  }

  getAvailableLineNumbersForDropOffs(color: Color, availableTurns: number[]) {
    const availableLinesForClearDrop = [];
    const availableLinesForDirtyDrop = [];
    const availableLinesForDrop = []; // result drops
    let firstStack;
    let lastStack;

    if (color === Color.White) {
      firstStack = 1;
      lastStack = 6;
    } else {
      firstStack = 19;
      lastStack = 24;
    }

    for (let i = firstStack; i <= lastStack; i++) {
      if (this.canDropClearFromLine(i, availableTurns, color)) {
        availableLinesForClearDrop.push(i);
      }
    }
    //try to calculate dirty drop off`s
    for (let i = firstStack; i <= lastStack; i++) {
      if (this.canDropDirtyFromLine(i, availableTurns, color)) {
        availableLinesForDirtyDrop.push(i);
      }
    }
    //get the most feather line from drop zone:
    const scope = this;
    if (availableLinesForDirtyDrop.length) {
      availableLinesForDirtyDrop.sort(function sortNumber(a, b) {
        return scope.getLengthFromDropZone(a, color) - scope.getLengthFromDropZone(b, color);
      });
      availableLinesForDrop.push(availableLinesForDirtyDrop[0]);
    }

    for (let i = 0; i < availableLinesForClearDrop.length; i++) {
      availableLinesForDrop.push(availableLinesForClearDrop[i]);
    }

    for (let i = 0; i < availableLinesForDirtyDrop.length; i++) {
      availableLinesForDrop.push(availableLinesForDirtyDrop[i]);
    }
    return availableLinesForDrop;
  }

  /**
   * @param {Integer} lineNumber source line number to check available drops
   * @param {Array} available dice turns
   * @param {Color} Color of player trying to drop
   * @return {Boolean} can user drop from this line clear
   */
  canDropClearFromLine(lineNumber: number, availableTurns: number[], color: Color) {
    if (!this.isHomeComplete(color)) {
      return false;
    }
    for (let i = 0; i < availableTurns.length; i++) {
      const num = color === Color.Black ? 25 - availableTurns[i] : availableTurns[i];
      if (lineNumber === num && this.canGetFromLine(this.stacks[lineNumber], color)) {
        return true;
      }
    }
    return false;
  }

  /**
   * @param {Integer} lineNumber source line number to check available drops
   * @param {Array} available dice turns
   * @param {Color} Color of player trying to drop
   * @return {Boolean} can user drop from this line dirty,
   */
  canDropDirtyFromLine(lineNumber: number, availableTurns: number[], color: Color) {
    if (!this.isHomeComplete(color)) {
      return false;
    }

    let maxTurn;

    if (availableTurns.length === 1) {
      maxTurn = availableTurns[0];
    } else if (availableTurns.length >= 2) {
      maxTurn = Math.max(availableTurns[0], availableTurns[1]);
    }

    if (this.canGetFromLine(this.stacks[lineNumber], color)) {
      //that is the featherest checker from bar
      if (
        this.isHomeLine(lineNumber, color) &&
        maxTurn &&
        this.getLengthFromDropZone(lineNumber, color) < maxTurn &&
        this.getMaxLengthFromDropZone(color) === this.getLengthFromDropZone(lineNumber, color)
      ) {
        return true;
      }
    }
  }

  getRestrictedTurns(availableTurns: number[], sourceLineNumber: number, color: Color) {
    const offBar = sourceLineNumber === 26 || sourceLineNumber === 27;
    const forwardSign = color === Color.Black ? 1 : -1;
    const restrictedTurns = [];

    this.saveModelState();
    //make firstMove, then check if we can move second turn.

    const modelStr = this.toString();

    if (availableTurns.length === 2) {
      //usual
      let firstTurn = availableTurns[0];
      let secondTurn = availableTurns[1];

      for (let i = 0; i < 2; i++) {
        //check first turn
        const move = new Move();
        move.setSourceLineNumber(sourceLineNumber);

        let currentInsertNumber;
        if (offBar) {
          currentInsertNumber = color === Color.Black ? firstTurn : 25 - firstTurn;
        } else {
          currentInsertNumber = sourceLineNumber + forwardSign * firstTurn;
        }
        if (currentInsertNumber < 0 || currentInsertNumber > 24) {
          this.restoreModelState();
          firstTurn = availableTurns[1];
          secondTurn = availableTurns[0];
          continue;
        }
        move.setInsertLineNumber(currentInsertNumber);
        move.color = color;
        if (this.canPlaceToLine(this.stacks[currentInsertNumber], color)) {
          this.makeForwardMove(move);

          if (!this.canMakeAnyForwardSingleMove(color, secondTurn)) {
            restrictedTurns.push(firstTurn);
          }
        }

        //get model to previousState
        this.restoreModelState();

        firstTurn = availableTurns[1];
        secondTurn = availableTurns[0];
      }
      this.restoreModelState();
    }
    if (this.toString() !== modelStr) {
      throw new Error('restricted turns calculation broke model state');
    }
    return restrictedTurns;
  }

  canMakeAnyForwardSingleMove(color: Color, turn: number) {
    // let forwardSign = color === Color.Black ? 1 : -1;

    if (this.getCheckersCountOnBar(color) > 0) {
      if (!this.canPlaceToLine(this.stacks[color === Color.Black ? turn : 25 - turn], color)) {
        return false;
      }
    }

    //check for all stacks except bars
    for (let i = 1; i < 25; i++) {
      const sourceStack = this.stacks[i];
      //that is my stack
      if (sourceStack.getCheckersCount() > 0 && sourceStack.getCheckersColor() === color) {
        if (this.getAvailableLineNumbersForDropOffs(color, [turn]).indexOf(sourceStack.getNumber()) !== -1) {
          return true;
        }

        if (this.getAvailableLineNumbersForSingleMoves([turn], sourceStack.getNumber(), color).length > 0) {
          return true;
        }
      }
    }

    return false;
  }

  canUseTurnsFully(color: Color, turns: number[]) {
    this.saveModelState();
    const forwardSign = color === Color.Black ? 1 : -1;
    for (let j = 1; j <= 27; j++) {
      const offBar = j === 26 || j === 27;
      if (
        (this.canGetFromLine(this.stacks[j], color) && !this.shouldMoveFromBar(color)) ||
        (this.shouldMoveFromBar(color) && offBar && this.canGetFromLine(this.stacks[j], color))
      ) {
        let firstTurn = turns[0];
        let secondTurn = turns[1];

        for (let i = 0; i < 2; i++) {
          //check first turn
          const move = new Move();
          move.setSourceLineNumber(j);

          let currentInsertNumber;
          if (offBar) {
            currentInsertNumber = color === Color.Black ? firstTurn : 25 - firstTurn;
          } else {
            currentInsertNumber = j + forwardSign * firstTurn;
          }
          if (currentInsertNumber < 0 || currentInsertNumber > 24) {
            this.restoreModelState();
            firstTurn = turns[1];
            secondTurn = turns[0];
            continue;
          }
          move.setInsertLineNumber(currentInsertNumber);
          move.color = color;
          if (this.canPlaceToLine(this.stacks[currentInsertNumber], color)) {
            this.makeForwardMove(move);
            if (this.canMakeAnyForwardSingleMove(color, secondTurn)) {
              this.restoreModelState();
              return true;
            }
          }

          //get model to previousState
          this.restoreModelState();
          firstTurn = turns[1];
          secondTurn = turns[0];
        }
      }
    }
    return false;
  }

  shouldMoveFromBar(playerColor: Color) {
    return this.getCheckersCountOnBar(playerColor) > 0;
  }

  getLengthFromDropZone(lineNumber: number, color: Color) {
    const dropZoneNumber = color === Color.Black ? 25 : 0;
    return Math.abs(dropZoneNumber - lineNumber);
  }

  getMaxLengthFromDropZone(color: Color) {
    let max = 0;
    for (let i = 1; i < 25; i++) {
      if (this.isBelongsToPlayer(this.stacks[i], color)) {
        if (this.getLengthFromDropZone(i, color) > max) {
          max = this.getLengthFromDropZone(i, color);
        }
      }
    }
    return max;
  }

  isHomeLine(lineNumber: number, color: Color) {
    let firstStack;
    let lastStack;
    if (color === Color.Black) {
      firstStack = 19;
      lastStack = 24;
    } else {
      firstStack = 1;
      lastStack = 6;
    }
    return firstStack <= lineNumber && lineNumber <= lastStack;
  }

  /**
   * returns, does the drop to this line is drop off board
   * @param {integer} insertNumber line number to insert
   */
  isDropOffBoard(insertNumber: number) {
    return insertNumber <= 0 || insertNumber >= 25;
  }

  /**
   * returns, does the drop to this line is "clear" drop off board
   * @param {integer} insertNumber line number to insert
   */
  isClearDropOffBoard(insertNumber: number) {
    return insertNumber === 0 || insertNumber === 25;
  }

  getCheckersCountOnBar(playerColor: Color) {
    return this.getBar(playerColor).getCheckersCount();
  }

  turnsSum(turns: number[]) {
    let sum = 0;
    for (let i = 0; i < turns.length; i++) {
      sum += turns[i];
    }
    return sum;
  }

  /**
   * saves model state, used in restricted moves calcuation
   */
  saveModelState() {
    this.modelSavedState = {};
    this.modelSavedState.stacks = [];
    for (let i = 0; i < 28; i++) {
      const stack = this.stacks[i];
      const count = stack.getCheckersCount();
      const color = stack.getCheckersColor();
      this.modelSavedState.stacks.push({ checkersCount: count, color: color });
    }
  }

  /**
   * restore model state, used in restricted moves calcuation
   */
  restoreModelState() {
    this.clearBoard();
    for (let i = 0; i < 28; i++) {
      const savedStack = this.modelSavedState.stacks[i];
      this.stacks[i].addCheckers(savedStack.color, savedStack.checkersCount);
    }
  }

  loadGame(status: number, turn: number, times: number[], moves: string) {
    // Uncomment to load premade game
    // turn = 0;
    // moves =
    //   'R24,R24,R23,R23,R17,R17,R21,R21,R20,W8,W8,W8,W9,W9,W9,W9,W9,W10,W13,W14,W27,W27,R20,R19,R19,R18,R18,R7||1,3,0,0|502';
    if (moves && moves.length > 10) {
      const v = moves.split('|');
      if (v.length === 4) {
        const sender = turn === 0 ? TableSides.SOUTH : TableSides.NORTH;
        this.onCommandReceived(Codes.GameCommands.INIT_TABLE, sender, v[0]);
        this.onCommandReceived(Codes.GameCommands.LOAD_HISTORY, sender, v[1]);
        this.onCommandReceived(Codes.GameCommands.LOAD_DICE, sender, v[2]);
        this.onCommandReceived(Codes.GameCommands.LOAD_STATE, sender, v[3]);
      }
    }

    if (this.table.getCountOfPlayers() === 1) {
      //TODO костыль, т.к. если во время реконнекта текущего игрока, оппонент сделает close and fortfeit, текущему игроку придет валидное состояние игры
      this.setRoundInProgress(false);
      this.setGameInProgress(false);
      this.setStatusMessage(t('opponent_left_table') + ' INVITE');
      this.onGameButtonsChanged.next([GameButton.INVITE]);
    } else {
      this.movesHistory.addRoll(
        this.getCurrentTurn(),
        Math.abs(this.dice.getFirstValue()),
        Math.abs(this.dice.getSecondValue())
      );
      this.refreshStatusMessage();
    }
  }

  onCommandReceived(cmd: any, sender: TableSides, obj: string): void {
    const str = obj.toString();
    switch (cmd) {
      case Codes.GameCommands.SHOW_PRICE:
        break;
      case Codes.GameCommands.INIT_TABLE:
        this.dice.setValues(-1, -1);
        this.diceSum[0] = 0;
        this.diceSum[1] = 0;
        this.setCurrentTurn(sender);

        if (!str || str.length === 0) {
          this.createInitialCheckers();
        } else {
          this.moveCheckersToPositions(str);
        }
        this.doubleCube.setLocked(false);
        this.doubleCube.setCubeState(undefined, 1);
        this.movesHistory.clearHistory();
        this.onInvalidateEvent.next(true);
        break;

      case Codes.GameCommands.LOAD_HISTORY:
        this.movesHistory.clearHistory();
        if (str) {
          const rawMoves = str.split(',');
          for (let i = 0; i < rawMoves.length; i++) {
            this.movesHistory.addMove(Move.fromString(rawMoves[i]));
          }
        }
        break;

      case Codes.GameCommands.LOAD_DICE:
        if (str && str.length) {
          const rawDice = str.split(',');
          const values = [];
          for (let i = 0; i < 4; i++) {
            values.push(0);
          }
          for (let i = 0; i < rawDice.length - 1; i++) {
            const value = parseInt(rawDice[i]);
            values[i] = value;
          }

          this.dice.setValuesFromArray(values);
        }
        break;

      case Codes.GameCommands.LOAD_STATE:
        const ph = parseInt(str);

        switch (ph) {
          case BackgammonPhases.ACTIVATE:
            this.setPhase(
              this.mySide === this.currentTurn ? BackgammonPhases.ACTIVATE : BackgammonPhases.DEACTIVATE
            );
            this.setGameInProgress(true);
            this.setRoundInProgress(true);
            break;
          case BackgammonPhases.CONTINUE:
            if (this.imWatching()) {
              this.setPhase(BackgammonPhases.WATCHER);
            } else {
              this.setPhase(
                this.mySide === this.currentTurn ? BackgammonPhases.CONTINUE : BackgammonPhases.WAIT_FOR_OP
              );
            }
            this.setGameInProgress(true);
            this.setRoundInProgress(true);
            break;
          case BackgammonPhases.CONTINUE1:
            if (this.imWatching()) {
              this.setPhase(BackgammonPhases.WATCHER);
            } else {
              this.setPhase(
                this.mySide === this.currentTurn ? BackgammonPhases.CONTINUE1 : BackgammonPhases.WAIT_FOR_OP1
              );
            }
            this.setGameInProgress(true);
            this.setRoundInProgress(true);
            break;
          case BackgammonPhases.SHOW_SCORE:
            this.setPhase(BackgammonPhases.SHOW_SCORE);
            this.setGameInProgress(true);
            this.setRoundInProgress(false);
            this.analyzePosition(false);
            break;
        }
        break;
      case Codes.GameCommands.SKIP:
        if (this.currentTurn) this.setCurrentTurn(this.reverseSide(this.currentTurn));
        if (this.imWatching()) {
          this.setPhase(BackgammonPhases.WATCHER);
        } else {
          this.setPhase(
            this.mySide === this.currentTurn ? BackgammonPhases.ACTIVATE : BackgammonPhases.DEACTIVATE
          );
        }
        break;
    }
  }

  onActionReceived(action: typeof GameAction) {
    if (action.cmd !== GameAction.START || action.cmd !== GameAction.REFUSED) {
      super.onActionReceived.apply(this, [action]);
    }

    const sender = action.player.getSeat(this.getTable()) === 0 ? TableSides.SOUTH : TableSides.NORTH;

    const string = String.fromCharCode.apply(null, action.data);
    switch (action.cmd) {
      case GameAction.CONFIRM:
        this.undos = 0;

        this.analyzePosition(false);
        this.setCurrentTurn(this.reverseSide(sender), true); // todo that is absent in java

        if (this.currentTurn !== this.reverseSide(sender)) {
          throw new Error('Current turn state is broken. It should be ' + this.reverseSide(sender));
        }
        break;
      case GameAction.DICE:
        const values = string.split(',');
        const dice1 = parseInt(values[0]);
        const dice2 = parseInt(values[1]);
        this.dice.setValues(dice1, dice2, true);

        this.diceSum[0] = parseInt(values[2]);
        this.diceSum[1] = parseInt(values[3]);

        if (this.imWatching()) {
          this.setPhase(BackgammonPhases.WATCHER);
        } else {
          this.setPhase(
            this.mySide === this.currentTurn ? BackgammonPhases.CONTINUE : BackgammonPhases.WAIT_FOR_OP
          );
        }
        this.analyzePosition(false);

        this.movesHistory.addRoll(this.getColorForSide(this.currentTurn!), dice1, dice2);

        this.sound.play(this.sound.codes.DICE, this.sound.categories.GLOBAL);

        break;
      case GameAction.DICE_FIRST:
        this.setCurrentTurn(TableSides.SOUTH);
        break;

      case GameAction.START:
        this.setCurrentTurn(sender);
        this.doubleCube.setCubeState(undefined, 1);
        this.doubleCube.setLocked(false);
        this.createInitialCheckers();
        this.setRoundInProgress(true);
        this.setGameInProgress(true);
        this.onInvalidateEvent.next(true);
        break;

      case GameAction.START_PRESSED:
        if (action.player.getPid() === this.me.getPid()) {
          this.setStatusMessage(t('waiting_for_opponent_to_start'));
          this.onGameButtonsChanged.next([]);
        } else {
          // todo showNotification?
        }
        break;

      case GameAction.CONTINUE:
        this.onTakeBackOffered.next(false);
        this.takebackOfferedToMe = false;
        this.deactivateTakeBackLock();

        if (action.player.getPid() === this.me.getPid()) {
          this.setStatusMessage(t('waiting_for_opponent_to_press') + ' ' + t('continue'));
          this.onGameButtonsChanged.next([]);
        } else {
          if (this.isContinuePressed()) {
            this.setStatusMessage(t('please_wait'));
            this.onGameButtonsChanged.next([]);
          } else {
            if (this.phase !== BackgammonPhases.SHOW_SCORE) {
              this.setStatusMessage(t('press') + ' continue');
              this.onGameButtonsChanged.next([GameButton.CONTINUE]);
            } else {
              this.refreshStatusMessage();
            }
          }
        }
        break;

      case GameAction.SKIP:
        if (this.currentTurn) this.setCurrentTurn(this.reverseSide(this.currentTurn));
        if (this.imWatching()) this.setPhase(BackgammonPhases.DEACTIVATE);
        else
          this.setPhase(
            this.mySide === this.currentTurn ? BackgammonPhases.ACTIVATE : BackgammonPhases.DEACTIVATE
          );
        break;

      case GameAction.OFFER_TAKEBACK:
        this.onTakeBackOffered.next(true);
        this.clock.startClock(
          action.player.getPid() !== this.me.getPid()
            ? this.getMyColor()
            : Color.reverseColor(this.getMyColor()),
          true
        );

        if (action.player.getPid() !== this.me.getPid()) {
          this.takebackOfferedToMe = true;
          this.showDoubleDialog();
        }
        break;
      case GameAction.DOUBLE_OK:
        this.onTakeBackOffered.next(false);
        this.takebackOfferedToMe = false;
        this.clock.startClock(this.getCurrentTurn(), true);
        this.setPhase(this.phase);
        this.hideReconnectionDialog();
        this.deactivateTakeBackLock();
        break;

      case GameAction.TAKEBACK:
        break;

      case GameAction.REFUSED:
        this.onTakeBackOffered.next(false);
        this.takebackOfferedToMe = false;
        this.setStatusMessage(t('request_declined_by_opponent'), 2000);
        this.deactivateTakeBackLock();
        break;

      case GameAction.OFFER_RESCUR:
      case GameAction.OFFER_RESDBL:
        if (action.player.getPid() !== this.me.getPid()) {
          if (this.autoRejectResigns) {
            this.gameService.sendPlay(
              Codes.PlayCommand.ACTION_REFUSED,
              action.player.getPid(),
              this.getTable().tid
            );
          } else {
            const title = t('opponents_request');
            const body = t('asks_resign_this_game_lose_points', action.player.getName(), string);
            const buttons = [
              {
                title: t('no'),
                onClick: () => {
                  this.gameService.sendPlay(
                    Codes.PlayCommand.ACTION_REFUSED,
                    action.player.getPid(),
                    this.getTable().tid
                  );
                  this.modalProvider.hide();
                },
              },
              {
                title: t('yes'),
                onClick: () => {
                  let command =
                    action.cmd === GameAction.OFFER_RESCUR
                      ? Codes.PlayCommand.RESCUR_OK
                      : Codes.PlayCommand.RESDBL_OK;
                  this.gameService.sendPlay(command, action.player.getPid(), this.getTable().tid);
                  this.modalProvider.hide();
                },
              },
              {
                title: t('dont_ask_me'),
                onClick: () => {
                  const command =
                    action.cmd === GameAction.OFFER_RESCUR
                      ? Codes.PlayCommand.RESCUR_OK
                      : Codes.PlayCommand.RESDBL_OK;
                  this.gameService.sendPlay(command, action.player.getPid(), this.getTable().tid);
                  this.autoRejectResigns = true;
                  this.modalProvider.hide();
                },
              },
            ];
            this.modalProvider.show({
              title,
              body,
              buttons,
            });
          }
        }
        break;
      case GameAction.BREAK:
        this.setRoundInProgress(false);
        this.setGameInProgress(false);
        break;
    }
  }

  reverseSide(side: TableSides) {
    if (side === TableSides.NORTH) return TableSides.SOUTH;
    return TableSides.NORTH;
  }

  isContinuePressed() {
    return this.continuePressed;
  }

  getPlayerForSide(side: TableSides) {
    return this.getPlayer(this.getColorForSide(side));
  }

  get opponent() {
    return this.getPlayer(this.getHisColor());
  }

  setCurrentTurn(turn: TableSides, restartClock?: boolean) {
    if (this.currentTurn !== turn) {
      const soundCategory =
        this.getCurrentTurn() === this.getMyColor()
          ? this.sound.categories.YOUR_MOVE
          : this.sound.categories.OPP_MOVE;
      this.sound.play(this.sound.codes.MOVE, soundCategory);
      this.movesHistory.setTurnChanged(true);
    }
    this.currentTurn = turn;
    this.onTurnChanged.next(this.currentTurn);
    this.clock.startClock(this.getColorForSide(turn), Boolean(restartClock));

    const northPlayer = this.getPlayer(this.getColorForSide(TableSides.NORTH));
    console.debug(
      'NORTH: ' +
        (this.getColorForSide(TableSides.NORTH) === Color.White ? 'White' : 'Black') +
        ' ' +
        (northPlayer ? northPlayer.name : 'none')
    );
    const southPlayer = this.getPlayer(this.getColorForSide(TableSides.SOUTH));
    console.debug(
      'SOUTH: ' +
        (this.getColorForSide(TableSides.SOUTH) === Color.White ? 'White' : 'Black') +
        ' ' +
        (southPlayer ? southPlayer.name : 'none')
    );
    console.debug('Turn changed, now its ' + this.currentTurn);

    this.checkInvariant();
  }

  checkInvariant() {
    for (let i = 1; i <= 24; ++i) {
      const checkers = this.stacks[i].checkers;
      if (checkers.length > 0) {
        const firstCheckerColor = checkers[0].color;
        for (let j = 1; j < checkers.length; ++j) {
          if (firstCheckerColor !== checkers[j].color) {
            (window as any).Console.sendLogToServer(new Error('Invariant violated'));
          }
        }
      }
    }
  }

  setPhase(phase: any) {
    if (this.phase !== phase) {
      this.continuePressed = false;
    }
    this.phase = phase;
    console.debug('setPhase: ' + phase.toString());

    this.roundWinner = this.getCurrentTurn();
    this.refreshStatusMessage();

    if (phase === BackgammonPhases.ACTIVATE) {
      if (!this.hasAnyMoves(this.getMyColor())) {
        //skip turn automatically if there are no possible moves
        // FIXME: canMove doesn't exist on type BaseGame
        // this.canMove = false;
        this.sendDice(0);
        this.sendDice(0);
      } else {
        console.error(this.preferencesService.getPreferences('AUTO_ROLL'));
        if (this.preferencesService.getPreferences('AUTO_ROLL')) {
          this.sendDice(0);
        }
      }
    }

    if (phase === BackgammonPhases.SHOW_SCORE) {
      this.setCurrentTurn(this.mySide, true);
      this.clock.startClock(this.getColorForSide(this.mySide), true);
    }
  }

  analyzePosition(afterReconnect: boolean) {
    if (!this.currentTurn) return;
    if (!this.hasMoves(this.getColorForSide(this.currentTurn), this.dice.getAvailableMoves())) {
      if (!afterReconnect) {
        if (
          this.mySide === this.currentTurn &&
          this.phase !== BackgammonPhases.CONTINUE1 &&
          !this.imWatching()
        ) {
          this.setPhase(BackgammonPhases.CONTINUE1);
          return;
        } else if (
          this.mySide !== this.currentTurn &&
          this.phase !== BackgammonPhases.WAIT_FOR_OP1 &&
          !this.imWatching()
        ) {
          this.setPhase(BackgammonPhases.WAIT_FOR_OP1);
          return;
        } else if (this.phase !== BackgammonPhases.WAIT_FOR_OP1 && this.imWatching()) {
          return;
        }
      }

      const enumSet = [
        BackgammonPhases.ACTIVATE,
        BackgammonPhases.DEACTIVATE,
        BackgammonPhases.CONTINUE1,
        BackgammonPhases.WAIT_FOR_OP1,
      ];

      if (this.isGameFinished()) {
        this.setPhase(BackgammonPhases.SHOW_SCORE);
      } else if (!afterReconnect || (this.phase && enumSet.indexOf(this.phase) === -1)) {
        this.setCurrentTurn(this.reverseSide(this.currentTurn!));
        if (!this.imWatching()) {
          if (this.mySide === this.currentTurn) {
            this.setPhase(BackgammonPhases.ACTIVATE);
          } else {
            if (!this.hasAnyMoves(this.getColorForSide(this.reverseSide(this.mySide)))) {
              this.setPhase(BackgammonPhases.DEACTIVATE);
            } else {
              if (this.dice.getAvailableMoves().length === 0) {
                this.setPhase(BackgammonPhases.DEACTIVATE);
              } else {
                this.movesHistory.addCantMove();
                this.setPhase(BackgammonPhases.NO_ANY_MOVE);
              }
            }
          }
        } else {
          if (this.dice.getAvailableMoves().length === 0) {
            this.setPhase(BackgammonPhases.DEACTIVATE);
          } else {
            this.movesHistory.addCantMove();
            this.setPhase(BackgammonPhases.NO_ANY_MOVE);
          }
        }
      }
    } else {
      if (this.getBar(this.getColorForSide(this.mySide)).getCheckersCount() !== 0) {
        if (!this.imWatching()) {
          this.setPhase(
            this.mySide === this.currentTurn ? BackgammonPhases.FIRST_FROM_BAR : BackgammonPhases.WAIT_FOR_OP
          );
        } else {
          this.setPhase(BackgammonPhases.WATCHER);
        }
      }
    }
  }

  setRoundInProgress(inProgress: boolean) {
    if (inProgress === true && this.roundInProgress.value !== inProgress) {
      // new round Started
      this.movesHistory && this.movesHistory.clearHistory();
    }

    if (this.roundInProgress.value === inProgress) {
      return;
    }

    this.roundInProgress.next(inProgress);
    if (inProgress) {
      console.debug(this.getLogPrefix() + 'Round started');
      this.onRoundStarted.next(true);
      this.clock.startClock(this.getColorForSide(this.getCurrentTurnSide()), true);
    } else {
      console.debug(this.getLogPrefix() + 'Round finished');
      this.onGameFinished.next(true);
    }
  }

  isGameFinished() {
    if (this.stacks[0].getCheckersCount() === 15 || this.stacks[25].getCheckersCount() === 15) return true;
    return false;
  }

  moveCheckersToPositions(str: string) {
    this.clearBoard();
    const rawCheckers = str.split(',');
    for (let i = 0; i < rawCheckers.length; i++) {
      const checker = Checker.fromString(rawCheckers[i]);
      if (checker) this.stacks[checker.getPosition()].attachChecker(checker);
    }
  }

  clearBoard() {
    for (let i = 0; i < this.stacks.length; i++) {
      this.stacks[i].clear();
    }
  }

  canPlaceToLine(stack: CheckerStack, playerColor: Color) {
    if (stack.getNumber() <= 0 || stack.getNumber() >= 25) {
      // drop off
      return this.isHomeComplete(playerColor);
    }
    return (
      this.isFree(stack) ||
      this.isUnderAttack(stack, playerColor) ||
      this.isBelongsToPlayer(stack, playerColor)
    );
  }

  canGetFromLine(stack: CheckerStack, playerColor: Color) {
    return !this.isFree(stack) && this.isBelongsToPlayer(stack, playerColor);
  }

  isUnderAttack(stack: CheckerStack, playerColor: Color) {
    return stack.getCheckersCount() === 1 && stack.getCheckersColor() !== playerColor;
  }

  isFree(stack: CheckerStack) {
    return stack.getCheckersCount() === 0;
  }

  isBelongsToPlayer(stack: CheckerStack, playerColor: Color) {
    return stack.topChecker() !== null && stack.getCheckersColor() === playerColor;
  }

  sendDice(delayMs: number) {
    if (this.phase === BackgammonPhases.ACTIVATE || this.phase === BackgammonPhases.CONTINUE1) {
      if (this.phase === BackgammonPhases.ACTIVATE) {
        this.phase = BackgammonPhases.CONTINUE;
      }
      this.setStatusMessage(t('sending'));
      if (delayMs > 0) {
        setTimeout(() => {
          const pid = this.me.getPid();
          const tid = this.getTable().tid;
          this.gameService.sendDice(pid, tid);
        }, delayMs);
      } else {
        const pid = this.me.getPid();
        const tid = this.getTable().tid;
        this.gameService.sendDice(pid, tid);
      }
    }
  }

  diceClick() {
    if (this.onTakeBackLockChanged.value === true) {
      return;
    } else if (this.takebackOfferedToMe) {
      this.showDoubleDialog();
    } else {
      this.sendDice(0);
      this.onGameButtonsChanged.next([]);
    }
  }

  canClickDoubleCube() {
    const enabled = this.doubleCube.isDoubleEnabled();
    const beforeRoll = this.getPhase() === BackgammonPhases.ACTIVATE;
    return enabled && beforeRoll;
  }

  doubleCubeClick() {
    if (!this.canClickDoubleCube()) return;

    const buttons = [
      { title: t('no'), onClick: () => this.modalProvider.hide() },
      {
        title: t('yes'),
        onClick: () => {
          if (this.doubleCube.isDoubleEnabled()) {
            this.doubleCube.setLocked(true);
            this.sendTakeBack();
            this.modalProvider.hide();
          }
        },
      },
    ];
    const body = t('you_selected_double_game');
    this.modalProvider.show({
      body,
      buttons,
    });
  }

  undoClick() {
    const move = this.movesHistory.getLastMove();
    if (!move) return;
    const moveBack = Move.reverseMove(move);
    if (move.getAdditionalMoves().length) {
      moveBack.addAdditionalMove(Move.reverseMove(move.getAdditionalMoves()[0]));
    }
    this.movesHistory.undoLastMove(moveBack);
    this.makeUndoMove(move);
    this.dice.undoTurn(moveBack.getDiceIndication()!);
    this.increaseUndos();
    const moves = [];
    moves.push(moveBack);
    this.onCheckerMoved.next(moves);
    this.emitCheckerMoved(moves, true);
    this.sendMoveToServer(moveBack);

    switch (this.phase) {
      case BackgammonPhases.WAIT_FOR_OP:
      case BackgammonPhases.WATCHER:
      case BackgammonPhases.WAIT_FOR_OP1:
      case BackgammonPhases.FIRST_FROM_BAR:
      case BackgammonPhases.CONTINUE:
      case BackgammonPhases.CONTINUE1:
        if (this.imWatching()) {
          this.setPhase(BackgammonPhases.WATCHER);
        } else {
          this.setPhase(
            this.mySide === this.currentTurn ? BackgammonPhases.CONTINUE : BackgammonPhases.WAIT_FOR_OP
          );
        }
        this.analyzePosition(false);
        this.refreshStatusMessage();
        break;
    }

    this.refreshStatusMessage();
    this.analyzePosition(false);
  }

  continueClick() {
    if (!this.continuePressed) {
      this.setCurrentTurn(this.reverseSide(this.mySide), true);
      this.gameService.sendPlay(Codes.PlayCommand.CONTINUE, this.me.getPid(), this.getTable().tid);
    }
  }

  emitCheckerMoved(moves: Move[], byClick?: boolean) {
    if (moves.length < 2) {
      //single move with attack or not
      if (moves[0].color === this.getMyColor()) {
        if (this.imPlaying() && !byClick) {
          moves[0].setSourceLineNumber(null);
        }
      }
      moves[0].byClick = true;
      this.onCheckerMoved.next(moves[0]);
    } else {
      //complex move, when you make two moves by one move
      if (moves[0].color === this.getMyColor()) {
        const complexMoveToView = new Move();
        complexMoveToView.setSourceLineNumber(null);
        complexMoveToView.setInsertLineNumber(moves[moves.length - 1].getInsertLineNumber());
        complexMoveToView.color = this.getMyColor();
        for (let i = 0; i < moves.length; i++) {
          if (moves[i].getAdditionalMoves().length > 0) {
            complexMoveToView.addAdditionalMove(moves[i].getAdditionalMoves()[0]);
          }
        }
        this.onCheckerMoved.next(complexMoveToView);
      }
    }
  }

  emitInvalidate() {
    this.onInvalidateEvent.next(true);
  }

  getMoveLength(move: Move) {
    if (move.sourceLineNumber === 26 || move.sourceLineNumber === 27) {
      //move from bar
      return move.color === Color.Black ? move.insertLineNumber : 25 - move.insertLineNumber;
    } else if (move.insertLineNumber === 26 || move.insertLineNumber === 27) {
      //undo move to bar
      return move.color === Color.Black ? move.sourceLineNumber : 25 - move.sourceLineNumber;
    } else if (move.insertLineNumber === 0 || move.insertLineNumber === 25) {
      //drop off board
      return move.color === Color.Black ? 25 - move.sourceLineNumber : move.sourceLineNumber;
    } else {
      return Math.abs(move.insertLineNumber - move.sourceLineNumber);
    }
  }

  getMyColor() {
    return this.getColorForSide(this.mySide);
  }

  getHisColor() {
    return Color.reverseColor(this.getMyColor());
  }

  getCurrentTurn() {
    return this.getColorForSide(this.currentTurn!);
  }

  getColorForSide(side: TableSides): Color {
    if (side === TableSides.SOUTH) return Color.White;
    return Color.Black;
  }

  getCurrentTurnSide(): TableSides {
    if (this.onTakeBackOffered.value === true) {
      return this.reverseSide(this.currentTurn!);
    } else {
      return this.currentTurn!;
    }
  }

  getPlayer(color: Color) {
    let side;
    if (color === this.getMyColor()) {
      side = this.mySide;
    } else {
      side = this.reverseSide(this.mySide);
    }
    return this.table.getPlayerBySeat(side === TableSides.SOUTH ? 0 : 1);
  }

  getScore(color: Color) {
    let side;
    if (color === this.getMyColor()) {
      side = this.mySide;
    } else {
      side = this.reverseSide(this.mySide);
    }
    return this.score[side];
  }

  getDiceSum(color: Color) {
    let side;
    if (color === this.getMyColor()) {
      side = this.mySide;
    } else {
      side = this.reverseSide(this.mySide);
    }
    return this.diceSum[side === TableSides.SOUTH ? 0 : 1] || 0;
  }

  getPips(color: Color) {
    let sum = 0;
    sum += 25 * this.getBar(color).getCheckersCount();

    if (color === Color.White) {
      for (let i = 1; i < 25; i++) {
        const checkers = this.stacks[i].getCheckersCount();
        if (checkers > 0 && this.stacks[i].topChecker()?.color === Color.White) sum += i * checkers;
      }
    } else {
      for (let i = 1; i < 25; i++) {
        const checkers = this.stacks[25 - i].getCheckersCount();
        if (checkers > 0 && this.stacks[25 - i].topChecker()?.color === Color.Black) sum += i * checkers;
      }
    }
    return sum;
  }

  isCrawfordRound() {
    const maxPoints = this.table.maxPoints;
    if (maxPoints === 1) return false;
    if (maxPoints - this.score[this.mySide] <= 1 && this.lastWinnerSide === this.mySide) return true;
    if (maxPoints - this.score[this.mySide] <= 1 && this.lastWinnerSide !== this.mySide) return true;
    return false;
  }

  getDice() {
    return this.dice;
  }

  getStacks() {
    return this.stacks;
  }

  getPhase() {
    return this.phase;
  }

  createInitialStacks() {
    for (let i = 0; i < this.stacksCount; i++) {
      this.stacks.push(new CheckerStack(i));
    }
  }

  createInitialCheckers() {
    for (let i = 0; i < 28; i++) {
      this.stacks[i].clear();
    }

    this.stacks[24].addCheckers(Color.White, 2);
    this.stacks[13].addCheckers(Color.White, 5);
    this.stacks[8].addCheckers(Color.White, 3);
    this.stacks[6].addCheckers(Color.White, 5);

    this.stacks[1].addCheckers(Color.Black, 2);
    this.stacks[12].addCheckers(Color.Black, 5);
    this.stacks[17].addCheckers(Color.Black, 3);
    this.stacks[19].addCheckers(Color.Black, 5);
  }

  isHomeComplete(color: Color) {
    let checkersInHome = 0;
    let firstStack = 0;
    let lastStack = 0;

    if (color === Color.White) {
      firstStack = 0;
      lastStack = 6;
    } else {
      firstStack = 19;
      lastStack = 25;
    }

    for (let i = firstStack; i <= lastStack; i++) {
      const stack = this.stacks[i];
      if (stack.getCheckersColor() !== null && stack.getCheckersColor() === color) {
        checkersInHome += stack.getCheckersCount();
      }
    }
    return checkersInHome === 15;
  }

  hasAnyMoves(playerColor: Color) {
    const anyDice = [1, 2, 3, 4, 5, 6];
    return this.hasMoves(playerColor, anyDice);
  }

  hasMoves(playerColor: Color, dices: number[]) {
    const playersBar = this.getBar(playerColor);
    // let homeComplete = this.isHomeComplete(playerColor);
    const hadCheckersOnBar = playersBar.getCheckersCount() > 0;
    // let forwardSign = playerColor === Color.Black ? 1 : -1;

    if (hadCheckersOnBar) {
      //есть фишки на баре
      for (let i = 0; i < dices.length; i++) {
        const stackNumberToPlace = playerColor === Color.Black ? dices[i] : 25 - dices[i];
        if (this.canPlaceToLine(this.stacks[stackNumberToPlace], playerColor)) {
          return true;
        }
      }
    } else {
      for (let i = 1; i <= 24; i++) {
        const stackFrom = this.stacks[i];
        if (stackFrom.topChecker() == null || stackFrom.getCheckersColor() !== playerColor) {
          //mean that is not my stack
          continue;
        }
        if (
          this.getAvailableMoves(i, playerColor, false, dices) &&
          this.getAvailableMoves(i, playerColor, false, dices)?.length
        ) {
          return true;
        }
      }
    }
    return false;
  }

  refreshStatusMessage() {
    let result = '';
    switch (this.phase) {
      case BackgammonPhases.UNKNOWN:
        break;
      case BackgammonPhases.ACTIVATE:
        if (!this.imWatching()) {
          result += t('select_dice_to_roll');
          this.onGameButtonsChanged.next([GameButton.ROLL]);
          this.doubleCube.emitDoubleCubeChangedEvent();
        } else {
          result += t(this.getCurrentTurn().getTitleRes());
          result += t('_s_turn_to_roll_dice');
        }
        break;
      case BackgammonPhases.DEACTIVATE:
        this.onGameButtonsChanged.next([]);
        if (!this.imWatching()) {
          result += t('wait_for_opponent_s_rolling_dice');
        } else {
          result += t(this.getCurrentTurn().getTitleRes());
          result += t('_s_turn_to_roll_dice');
        }
        break;
      case BackgammonPhases.CONTINUE:
        this.onGameButtonsChanged.next([]);
        result += t('move') + ' ';
        const availableMoves = this.dice.getAvailableMoves();
        for (let i = 0; i < availableMoves.length; i++) {
          if (i !== 0) result += ' : ';
          result += availableMoves[i].toString();
        }
        if (this.dice.getConsumedMoves().length > 0 && this.canUndo()) {
          result += ' ' + t('or') + ' ' + t('undo');
          this.onGameButtonsChanged.next([GameButton.UNDO]);
        }
        break;

      case BackgammonPhases.CONTINUE1:
        const rollComplete = this.dice.getAvailableMoves().length === 0;
        if (rollComplete) {
          result += t('select_dice_to_confirm');
          this.onGameButtonsChanged.next([GameButton.DONE]);
          if (this.canUndo()) {
            result += ' ' + t('or') + ' ' + t('undo');
            this.onGameButtonsChanged.next([GameButton.DONE, GameButton.UNDO]);
          }
        } else {
          const someMovesMade = this.dice.getConsumedMoves().length > 0;
          let movesMadeOnlyFromBar = true;

          for (let i = 0; i < this.movesHistory.getMoves().length; i++) {
            if (
              this.movesHistory.getMoves()[i].getSourceLineNumber() !== 26 &&
              this.movesHistory.getMoves()[i].getSourceLineNumber() !== 27
            ) {
              movesMadeOnlyFromBar = false;
            }
          }
          if (someMovesMade && !movesMadeOnlyFromBar) {
            result += t('no_moves_select_dice_to_confirm');
            this.onGameButtonsChanged.next([GameButton.DONE]);
            if (this.canUndo()) {
              result += ' ' + t('or') + ' ' + t('undo');
              this.onGameButtonsChanged.next([GameButton.DONE, GameButton.UNDO]);
            }
          } else {
            result += t('no_moves');
            const delay = someMovesMade ? 1000 : 2000;
            this.sendDice(delay);
            this.onGameButtonsChanged.next([]);
          }
        }
        break;
      case BackgammonPhases.WAIT_FOR_OP:
        this.onGameButtonsChanged.next([]);
        if (!this.imWatching()) {
          result += t('opponent_to_move_') + ' ';
          const availableMoves = this.dice.getAvailableMoves();

          for (let i = 0; i < availableMoves.length; i++) {
            if (i !== 0) result += ' : ';

            result += availableMoves[i];
          }
        }
        break;
      case BackgammonPhases.WAIT_FOR_OP1:
        this.onGameButtonsChanged.next([]);
        const someMovesMade = this.dice.getConsumedMoves().length > 0;
        if (someMovesMade) {
          result += t('waiting_for') + ' ';
          if (!this.imWatching()) {
            result += t('opponent');
          } else {
            result += t(this.getCurrentTurn().getTitleRes());
          }
          result += ' ' + t('to_confirm');
        } else {
          result += t('opponent_has_no_moves');
        }
        break;
      case BackgammonPhases.WATCHER:
        this.onGameButtonsChanged.next([]);
        if (this.dice.getAvailableMoves().length) {
          result += t('_s_available_moves_', t(this.getCurrentTurn().getTitleRes()));
          for (let i = 0; i < this.dice.getAvailableMoves().length; i++) {
            result += ' : ';
            result += this.dice.getAvailableMoves()[i];
          }
        } else {
          result += ' ' + t('waiting_for');
          result += ' ' + t(this.getCurrentTurn().getTitleRes());
          result += ' ' + t('to_confirm');
        }
        break;
      case BackgammonPhases.WANT_TO_START:
        break;
      case BackgammonPhases.SHOW_SCORE:
        if (this.imWatching()) {
          result += t(this.getCurrentTurn().getTitleRes());
          result += ' ';
          result += t('won_');
        } else {
          if (this.isGameInProgress()) {
            result += t('you');
            result += ' ';
            if (this.roundWinner) {
              result += this.roundWinner === this.getMyColor() ? t('won_') : t('lost_');
            } else {
              result += this.getCurrentTurn() === this.getMyColor() ? t('won_') : t('lost_');
            }
            result += ' ';
            result += t('press');
            result += ' continue';
            this.onGameButtonsChanged.next([GameButton.CONTINUE]);
          } else {
            result = t('game_ended') + '. ' + t('press_new_game');
            this.onGameButtonsChanged.next([GameButton.NEW_GAME]);
          }
        }
        break;
      case BackgammonPhases.NO_ANY_MOVE:
        if (this.imWatching()) {
          result += t(Color.reverseColor(this.getCurrentTurn()).getTitleRes());
          result += ' ';
          result += t('cannot_make_any_move');
        } else {
          if (!this.hasMoves(this.getColorForSide(this.currentTurn!), this.dice.getAvailableMoves())) {
            //opponent has no moves
            this.movesHistory.addCantMove();
            result += t('opponent_has_no_moves');
          } else {
            result += t('wait_for_opponents_move');
          }
        }
        break;
      case BackgammonPhases.FIRST_FROM_BAR:
        if (this.imWatching()) {
          result += t(this.getCurrentTurn().getTitleRes());
          result += t(' ');
          result += t('has_to_move_from_the_bar');
        } else {
          result += t('you_have_to_move_from_the_bar');
        }
        break;
    }

    if (result.length > 0) {
      this.setStatusMessage(result);
    }
  }

  onScoresReceived(scores: any) {
    const score = scores.score;

    if (score.length === 0) {
      this.diceSum[0] = 0;
      this.diceSum[1] = 0;
      this.doubleCube.setCubeState(undefined, 1);
      this.lastWinnerSide = null;
      this.score[TableSides.NORTH] = 0;
      this.score[TableSides.SOUTH] = 0;
      return;
    }

    switch (scores.type) {
      case BackgammonScoreTypes.LAST:
        if (this.score[TableSides.SOUTH] < score[0] && this.score[TableSides.NORTH] >= score[1]) {
          this.lastWinnerSide = TableSides.SOUTH;
        } else if (this.score[TableSides.NORTH] < score[1] && this.score[TableSides.SOUTH] >= score[0]) {
          this.lastWinnerSide = TableSides.NORTH;
        } else {
          this.lastWinnerSide = null;
        }

        this.score[TableSides.SOUTH] = score[0];
        this.score[TableSides.NORTH] = score[1];
        this.onScoreChanged.next();
        break;
      case BackgammonScoreTypes.FULL:
      case BackgammonScoreTypes.FULL_REV:
        this.diceSum[0] = score[0];
        this.diceSum[1] = score[1];
        let doubleCubeOwner;
        switch (score[3]) {
          case 0:
            doubleCubeOwner = Color.Black;
            break;
          case 1:
            doubleCubeOwner = Color.White;
            break;
          default:
            doubleCubeOwner = undefined;
            break;
        }
        this.doubleCube.setLocked(false);
        this.doubleCube.setCubeState(doubleCubeOwner, score[2]);
        switch (score[4]) {
          case 0:
            this.lastWinnerSide = TableSides.SOUTH;
            break;
          case 1:
            this.lastWinnerSide = TableSides.NORTH;
            break;
          default:
            this.lastWinnerSide = null;
            break;
        }
        this.score[TableSides.SOUTH] = score[5];
        this.score[TableSides.NORTH] = score[6];
        this.onScoreChanged.next();
        break;
    }
  }

  canUndo() {
    const availableTurns = this.dice.getAvailableMoves();
    const consumedTurns = this.dice.getConsumedMoves();
    if (availableTurns && consumedTurns && availableTurns.length + consumedTurns.length === 4) {
      return this.undos < 10;
    } else {
      return this.undos < 6;
    }
  }

  increaseUndos() {
    this.undos++;
  }

  /**
   * returns color string
   * @param {Color} color
   */
  getStringFromColor(color: Color) {
    return color.toString();
  }

  getBar(color: Color) {
    if (color === Color.White) {
      return this.stacks[27];
    } else {
      return this.stacks[26];
    }
  }

  sendTakeBack() {
    this.gameService.sendPlay(Codes.PlayCommand.TAKEBACK, this.me.getPid(), this.getTable().tid);
    this.setStatusMessage(t('waiting_for_opponent_confirmation'));
    const timeout = (this.getTable().timePerMove - 5) * 1000;
    this.setStatusMessage(t('waiting_for_opponent_confirmation'), timeout);
    this.activateTakeBackLock(timeout);
  }

  activateTakeBackLock(timeout: number) {
    this.onTakeBackLockChanged.next(true);
    this.takeBackLockTimer = setTimeout(() => this.onTakeBackLockChanged.next(false), timeout);
  }

  deactivateTakeBackLock() {
    this.onTakeBackLockChanged.next(false);
    if (this.takeBackLockTimer) clearTimeout(this.takeBackLockTimer);
  }

  isMyTable() {
    return this.table.getOwner()?.pid === this.me.pid;
  }

  showDoubleDialog() {
    const player = this.opponent;
    if (!player) return;

    const buttons = [
      {
        title: t('take'),
        onClick: () => {
          // localStorage.setItem("canResign", true);
          this.gameService.sendPlay(Codes.PlayCommand.TAKEBACK_OK, player.getPid(), this.getTable().tid);
          this.modalProvider.hide();
        },
      },
      {
        title: t('drop'),
        onClick: () => {
          // localStorage.setItem("canResign", true);
          this.gameService.sendPlay(Codes.PlayCommand.TAKEBACK_NOK, player.getPid(), this.getTable().tid);
          this.modalProvider.hide();
        },
      },
      {
        title: t('view_board'),
        onClick: () => {
          this.setStatusMessage(t('ask_double_game'));
          this.onGameButtonsChanged.next([GameButton.SHOW_DOUBLE_DIALOG]);
          this.modalProvider.hide();
        },
      },
    ];

    const body = t('ask_double_game', player.getName());
    const title = t('opponents_request');

    this.modalProvider.show({
      title,
      body,
      buttons,
    });
  }

  getBlackPlayer() {
    return this.getPlayer(Color.Black);
  }

  getWhitePlayer(): IPlayer {
    return this.getPlayer(Color.White)!;
  }

  getMoveOnDoubleClickPref() {
    return this.preferencesService.getPreferences('DOUBLE_CLICK_MOVE');
  }

  getMoveHistory() {
    return this.movesHistory;
  }

  getDoubleCube() {
    return this.doubleCube;
  }

  saveFinalScore() {
    this.finalScore = [this.getScore(this.getMyColor()), this.getScore(this.getHisColor())];
  }

  getFinalScore() {
    return this.finalScore;
  }

  canMove(): boolean {
    throw new Error('Method not implemented.');
  }

  onDealReceived(deal: any) {
    if (deal.type === 4) {
      this.sound.play(this.sound.codes.MOVE, this.sound.categories.OPP_MOVE);
      this.setPhase(BackgammonPhases.SHOW_SCORE);
      this.setRoundInProgress(false);
    }
  }
}
